(function () {
  'use strict';

  const parseJsonNode = function (root, selector, fallback) {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const runWhenNear = function (element, callback, rootMargin) {
    if (!element || typeof callback !== 'function') return;
    if (!("IntersectionObserver" in window)) {
      callback();
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        callback();
      });
    }, { rootMargin: rootMargin || '320px 0px' });

    observer.observe(element);
  };

  function initHero(root) {
    const hero = root.matches?.('[data-bvm-home-hero]') ? root : root.querySelector?.('[data-bvm-home-hero]');
    if (!hero || hero.dataset.bvmHeroReady === 'true') return;
    hero.dataset.bvmHeroReady = 'true';

    const video = hero.querySelector('[data-bvm-home-video]');
    if (!video) return;

    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    const desktopSrc = video.dataset.desktopSrc || '';
    const mobileSrc = video.dataset.mobileSrc || '';
    const chosenSrc = window.matchMedia && window.matchMedia('(max-width: 749px)').matches && mobileSrc ? mobileSrc : desktopSrc;
    if (!chosenSrc) return;

    const revealVideo = function () {
      hero.classList.add('is-video-ready');
      hero.classList.remove('is-video-loading');
    };

    const loadVideo = function () {
      if (video.dataset.bvmSourceLoaded === 'true') return;
      video.dataset.bvmSourceLoaded = 'true';
      hero.classList.add('is-video-loading');
      video.src = chosenSrc;
      video.load();
      video.addEventListener('loadeddata', revealVideo, { once: true });
      video.addEventListener('playing', revealVideo, { once: true });
      video.play().catch(function () {
        hero.classList.remove('is-video-loading');
        /* The lightweight poster remains visible when autoplay is blocked. */
      });
    };

    const scheduleLoad = function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadVideo, { timeout: 1400 });
      } else {
        window.setTimeout(loadVideo, 350);
      }
    };

    if (document.readyState === 'complete') scheduleLoad();
    else window.addEventListener('load', scheduleLoad, { once: true });
  }

  function initCompare(root) {
    const compares = root.matches?.('[data-bvm-home-compare]') ? [root] : root.querySelectorAll?.('[data-bvm-home-compare]') || [];
    compares.forEach(function (compare) {
      if (compare.dataset.bvmCompareReady === 'true') return;
      compare.dataset.bvmCompareReady = 'true';
      const range = compare.querySelector("input[type=\"range\"]");
      if (!range) return;
      const update = function () {
        compare.style.setProperty('--bvm-compare-position', range.value + '%');
      };
      range.addEventListener('input', update, { passive: true });
      range.addEventListener('change', update, { passive: true });
      update();
    });
  }

  const normalizeCountryToken = function (value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase()
      .replace(/&/g, 'AND')
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const countryAliases = Object.freeze({
    'UNITED-STATES': 'US',
    'UNITED-STATES-OF-AMERICA': 'US',
    'UNITED-STATES-AMERICA': 'US',
    'U-S-A': 'US',
    'USA': 'US',
    'AMERICA': 'US',
    'UNITED-KINGDOM': 'GB',
    'GREAT-BRITAIN': 'GB',
    'UK': 'GB',
    'ENGLAND': 'GB',
    'SOUTH-KOREA': 'KR',
    'REPUBLIC-OF-KOREA': 'KR',
    'KOREA-SOUTH': 'KR',
    'UAE': 'AE',
    'UNITED-ARAB-EMIRATES': 'AE',
    'CZECH-REPUBLIC': 'CZ',
    'CZECHIA': 'CZ',
    'THE-NETHERLANDS': 'NL',
    'NETHERLANDS': 'NL'
  });

  const normalizeCodeList = function (raw, fallback) {
    let value = raw;
    if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.value)) {
      value = value.value;
    }
    if (!Array.isArray(value)) {
      value = typeof value === 'string' ? value.split(/[\s,;|]+/) : [];
    }
    if (!value.length && fallback) value = String(fallback).split(/[\s,;|]+/);

    return Array.from(new Set(value.map(function (code) {
      return String(code || '').replace(/["'\[\]]/g, '').trim().toUpperCase();
    }).filter(Boolean)));
  };

  const resolveCountryCoordinate = function (country, coordinates) {
    const candidates = [country?.code, country?.key, country?.name]
      .map(normalizeCountryToken)
      .filter(Boolean);

    for (const candidate of candidates) {
      const alias = countryAliases[candidate] || candidate;
      const coordinate = coordinates[alias];
      if (coordinate) return coordinate;
    }
    return null;
  };

  const mergeBuildCountries = function (countries, coordinates) {
    const merged = new Map();

    (Array.isArray(countries) ? countries : []).forEach(function (country) {
      const coordinate = resolveCountryCoordinate(country, coordinates);
      if (!coordinate) return;

      const iso2 = String(coordinate.iso2 || country?.code || country?.key || '')
        .trim()
        .toUpperCase();
      if (!iso2) return;

      const count = Math.max(1, Number.parseInt(country?.count || '1', 10) || 1);
      const existing = merged.get(iso2);
      if (existing) {
        existing.count += count;
        if (!existing.name && country?.name) existing.name = String(country.name).trim();
        return;
      }

      merged.set(iso2, {
        code: iso2,
        name: String(coordinate.name || country?.name || iso2).trim(),
        count: count,
        coordinate: coordinate
      });
    });

    return Array.from(merged.values());
  };

  const calculateMarkerSizes = function (count, minimumCount, maximumCount) {
    const safeCount = Math.max(1, Number(count) || 1);
    const safeMinimum = Math.max(1, Number(minimumCount) || 1);
    const safeMaximum = Math.max(safeMinimum, Number(maximumCount) || safeMinimum);
    let ratio = 0;

    if (safeMaximum > safeMinimum) {
      ratio = (Math.sqrt(safeCount) - Math.sqrt(safeMinimum)) /
        (Math.sqrt(safeMaximum) - Math.sqrt(safeMinimum));
    }
    ratio = Math.max(0, Math.min(1, ratio));

    return {
      desktop: Number((6 + (11 * ratio)).toFixed(2)),
      mobile: Number((5 + (9 * ratio)).toFixed(2))
    };
  };

  const enhanceMap = async function (map) {
    if (!map || map.dataset.bvmMapReady === 'true') return;

    const coordinates = window.BVM_COUNTRY_COORDINATES || {};
    if (!Object.keys(coordinates).length) {
      const attempts = Number(map.dataset.bvmMapAttempts || 0);
      if (attempts < 30) {
        map.dataset.bvmMapAttempts = String(attempts + 1);
        window.setTimeout(function () { enhanceMap(map); }, 120);
      }
      return;
    }

    map.dataset.bvmMapReady = 'true';
    const markerLayer = map.querySelector('[data-bvm-home-map-markers]');
    const tooltip = map.querySelector('[data-bvm-home-map-tooltip]');
    const tooltipCountry = map.querySelector('[data-bvm-home-map-country]');
    const tooltipCount = map.querySelector('[data-bvm-home-map-count]');
    if (!markerLayer || !tooltip || !tooltipCountry || !tooltipCount) return;

    const rawSalesCountries = parseJsonNode(map, '[data-bvm-home-sales-countries]', []);
    const rawBuildCountries = parseJsonNode(map, '[data-bvm-home-build-countries]', []);
    const salesCountries = normalizeCodeList(rawSalesCountries, map.dataset.fallbackCodes || '');
    const buildCountries = mergeBuildCountries(rawBuildCountries, coordinates);
    const shippedIso2 = new Set();

    salesCountries.forEach(function (rawCode) {
      const coordinate = resolveCountryCoordinate({ code: rawCode }, coordinates);
      const iso2 = String(coordinate?.iso2 || rawCode || '').trim().toUpperCase();
      if (iso2) shippedIso2.add(iso2);
    });
    buildCountries.forEach(function (country) { shippedIso2.add(country.code); });

    const mapUrl = map.dataset.mapUrl;
    const fallbackImage = map.querySelector('[data-bvm-home-map-fallback]');
    fallbackImage?.removeAttribute('title');

    if (mapUrl) {
      try {
        const response = await fetch(mapUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Map request failed: ' + response.status);
        const text = await response.text();
        const documentSvg = new DOMParser().parseFromString(text, 'image/svg+xml');
        const svg = documentSvg.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') throw new Error('Invalid SVG map');

        svg.classList.add('bvm-home-map__inline-map');
        svg.querySelectorAll('title').forEach(function (node) { node.remove(); });
        svg.removeAttribute('aria-labelledby');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.removeAttribute('role');
        map.prepend(svg);
        fallbackImage?.remove();

        svg.querySelectorAll('[data-iso2], [data-iso3]').forEach(function (path) {
          const iso2 = String(path.dataset.iso2 || '').trim().toUpperCase();
          const iso3 = String(path.dataset.iso3 || '').trim().toUpperCase();
          const coordinate = coordinates[iso2] || coordinates[iso3];
          const canonicalIso2 = String(coordinate?.iso2 || iso2).trim().toUpperCase();
          const isShipped = shippedIso2.has(canonicalIso2);
          path.classList.toggle('is-shipped', isShipped);
          if (isShipped) path.setAttribute('data-bvm-shipped', 'true');
          else path.removeAttribute('data-bvm-shipped');
        });
      } catch (error) {
        console.warn('[Bonvent Home] World map enhancement unavailable', error);
      }
    }

    markerLayer.replaceChildren();
    const counts = buildCountries.map(function (country) { return country.count; });
    const minimumCount = counts.length ? Math.min.apply(null, counts) : 1;
    const maximumCount = counts.length ? Math.max.apply(null, counts) : 1;
    let activeRecord = null;

    const records = buildCountries
      .slice()
      .sort(function (a, b) { return b.count - a.count; })
      .map(function (country) {
        const coordinate = country.coordinate;
        if (!coordinate || !Number.isFinite(coordinate.xPercent) || !Number.isFinite(coordinate.yPercent)) return null;

        const marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'bvm-home-map__marker';
        marker.style.left = coordinate.xPercent + '%';
        marker.style.top = coordinate.yPercent + '%';
        marker.dataset.countryCode = country.code;
        marker.dataset.buildCount = String(country.count);
        marker.setAttribute('aria-label', country.name + ': ' + country.count + ' customer build' + (country.count === 1 ? '' : 's'));

        const dot = document.createElement('span');
        dot.className = 'bvm-home-map__marker-dot';
        dot.setAttribute('aria-hidden', 'true');
        marker.appendChild(dot);

        const sizes = calculateMarkerSizes(country.count, minimumCount, maximumCount);
        marker.style.setProperty('--bvm-dot-size', sizes.desktop + 'px');
        marker.style.setProperty('--bvm-dot-size-mobile', sizes.mobile + 'px');
        marker.style.setProperty('--bvm-hit-size', Math.max(20, sizes.desktop + 12) + 'px');
        marker.style.setProperty('--bvm-hit-size-mobile', Math.max(18, sizes.mobile + 11) + 'px');
        markerLayer.appendChild(marker);

        return { country: country, marker: marker, coordinate: coordinate, x: 0, y: 0 };
      })
      .filter(Boolean);

    const positionTooltip = function (record) {
      if (!record || tooltip.hidden) return;
      const width = map.clientWidth;
      const height = map.clientHeight;
      const tooltipWidth = tooltip.offsetWidth || 180;
      const tooltipHeight = tooltip.offsetHeight || 68;
      const margin = 10;
      const left = Math.min(width - (tooltipWidth / 2) - margin, Math.max((tooltipWidth / 2) + margin, record.x));
      const showBelow = record.y - tooltipHeight - 20 < margin;
      const top = Math.min(height - margin, Math.max(margin, record.y));
      tooltip.style.left = left + 'px';
      tooltip.style.top = top + 'px';
      tooltip.classList.toggle('is-below', showBelow);
    };

    const layoutMarkers = function () {
      const width = map.clientWidth;
      const height = map.clientHeight;
      if (!width || !height) return;
      records.forEach(function (record) {
        record.x = (record.coordinate.xPercent / 100) * width;
        record.y = (record.coordinate.yPercent / 100) * height;
      });
      if (activeRecord) positionTooltip(activeRecord);
    };

    const showTooltip = function (record) {
      if (!record) return;
      activeRecord = record;
      tooltipCountry.textContent = record.country.name || record.country.code;
      tooltipCount.textContent = record.country.count + ' customer build' + (record.country.count === 1 ? '' : 's');
      tooltip.hidden = false;
      window.requestAnimationFrame(function () { positionTooltip(record); });
    };

    const hideTooltip = function () {
      activeRecord = null;
      tooltip.hidden = true;
      tooltip.classList.remove('is-below');
    };

    records.forEach(function (record) {
      record.marker.addEventListener('pointerenter', function () { showTooltip(record); });
      record.marker.addEventListener('pointerleave', function () {
        if (document.activeElement !== record.marker) hideTooltip();
      });
      record.marker.addEventListener('focus', function () { showTooltip(record); });
      record.marker.addEventListener('blur', function () { window.setTimeout(hideTooltip, 0); });
      record.marker.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        showTooltip(record);
      });
    });

    if (typeof ResizeObserver === 'function') new ResizeObserver(layoutMarkers).observe(map);
    window.addEventListener('resize', layoutMarkers, { passive: true });
    document.addEventListener('click', function (event) {
      if (!map.contains(event.target)) hideTooltip();
    });
    window.requestAnimationFrame(layoutMarkers);
  };

  function initMaps(root) {
    const maps = root.matches?.('[data-bvm-home-map]') ? [root] : root.querySelectorAll?.('[data-bvm-home-map]') || [];
    maps.forEach(function (map) {
      if (map.dataset.bvmMapScheduled === 'true') return;
      map.dataset.bvmMapScheduled = 'true';
      runWhenNear(map, function () { enhanceMap(map); }, '420px 0px');
    });
  }

  function initAll(root) {
    const scope = root || document;
    initHero(scope);
    initCompare(scope);
    initMaps(scope);
  }

  document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  if (document.readyState !== 'loading') initAll(document);
}());
